import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatCurrency } from 'support/helpers/currency/currency';
import { isDefined } from 'support/helpers/generic/generic';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { TransactionsRoutes } from 'support/types';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { SearchInput } from 'components/SearchInput/SearchInput';

import { PaymentReconciliationDetailsData } from '../../types';
import { DiscrepancyBadge } from './components/DiscrepancyBadge';
import { PaymentReconciliationLineItemsStatusBadge } from './components/PaymentReconciliationLineItemsStatusBadge';

type PaymentReconciliationLineItem = PaymentReconciliationDetailsData['data']['line_items'][number];
type PaymentReconciliationLineItemsProps = {
  data: Array<PaymentReconciliationLineItem>;
  hasMultiplePartners: boolean;
  currency: string | undefined | null;
  isLoading: boolean;
};

export const PaymentReconciliationLineItems: FC<PaymentReconciliationLineItemsProps> = ({
  data,
  hasMultiplePartners,
  currency,
  isLoading,
}) => {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const { t } = useTranslation();

  const tableHeaders: Array<TableHeader> = [
    { label: t('paymentReconciliation:detailPage.list.tableHeaders.documentExternalRef') },
    { label: t('paymentReconciliation:detailPage.list.tableHeaders.documentType') },
    hasMultiplePartners ? { label: t('paymentReconciliation:detailPage.list.tableHeaders.tradePartner') } : null,
    { label: t('paymentReconciliation:detailPage.list.tableHeaders.amount'), alignRight: true },
    { label: t('paymentReconciliation:detailPage.list.tableHeaders.discrepancy'), alignRight: true },
    { label: t('paymentReconciliation:detailPage.list.tableHeaders.status') },
  ].filter(isDefined);

  const buildRow = (item: PaymentReconciliationLineItem, index: number): Array<TableItem> => {
    return [
      {
        element: item.message_external_ref || EMPTY_FIELD,
        className: 'max-w-xs truncate',
        key: `message-${item.message_id}-${index}`,
      },

      {
        element: t(`common:messageTypes.singular.${item.message_type}`),
        key: 'message-type',
      },
      hasMultiplePartners
        ? {
            key: 'trade-partner',
            element: item.trade_partner.name || EMPTY_FIELD,
            className: 'max-w-xs truncate',
          }
        : null,
      {
        element: (
          <span className="tabular-nums">
            {item.line_due_amount ? formatCurrency(item.line_due_amount, currency) : EMPTY_FIELD}
          </span>
        ),
        className: 'text-end',
        key: 'amount',
      },
      {
        element: <DiscrepancyBadge item={item} currency={currency} />,
        key: 'discrepancy',
        className: 'text-end',
      },
      {
        element: <PaymentReconciliationLineItemsStatusBadge item={item} />,
        key: 'status',
      },
    ].filter(isDefined);
  };

  const listData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter((item) =>
      item.message_external_ref.toLocaleLowerCase().includes(searchFilter.toLocaleLowerCase()),
    );
  }, [data, searchFilter]);

  return (
    <div className="flex flex-col gap-3">
      <div className="w-[260px]">
        <SearchInput
          filter={searchFilter}
          onFilterChange={setSearchFilter}
          placeholder={t('paymentReconciliation:detailPage.list.search.placeholder', { count: data.length })}
        />
      </div>
      <ListTable
        headers={tableHeaders}
        data={listData}
        rowBuilder={buildRow}
        rowClickPath={(item) => ({
          url: routeToPage(TransactionsRoutes.viewMessage, { id: item.message_id }),
          state: { keepBreadcrumbs: true },
        })}
        emptyState={
          <ListTable.EmptyState background="gray" cols={tableHeaders.length}>
            {t('paymentReconciliation:detailPage.list.emptyState')}
          </ListTable.EmptyState>
        }
        isLoading={isLoading}
      />
    </div>
  );
};
